import axios from 'axios';

class MaintenanceService {
  constructor() {
    this.pollInterval = 10000; // 10s
    this.timer = null;
    this.listeners = new Set();
    this.current = null;
    this.eventSource = null;
  }

  startPolling() {
    // Do not start polling if SSE is active
    if (this.eventSource) return;
    if (this.timer) return;
    this.check();
    this.timer = setInterval(() => this.check(), this.pollInterval);
  }

  stopPolling() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  async check() {
    try {
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/maintenance`;
      const res = await axios.get(apiUrl, { timeout: 5000 });
      const data = res.data || {};
      const prev = this.current ? JSON.stringify(this.current) : null;
      const next = JSON.stringify(data);
      if (prev !== next) {
        this.current = data;
        this.emit(data);
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          try {
            window.dispatchEvent(new CustomEvent('maintenance:update', { detail: data }));
          } catch (e) {
            // ignore CustomEvent creation errors
          }
        }
      }
    } catch (err) {
      // transient network or CORS errors; ignore to avoid noisy logs
    }
  }

  startSSE() {
    if (this.eventSource) return;
    if (typeof window === 'undefined' || !window.EventSource) return;
    try {
      const url = `${process.env.REACT_APP_API_URL}/api/maintenance/stream`;
      this.eventSource = new EventSource(url);

      this.eventSource.addEventListener('maintenance', (e) => {
        try {
          const data = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
          const prev = this.current ? JSON.stringify(this.current) : null;
          const next = JSON.stringify(data);
          if (prev !== next) {
            this.current = data;
            this.emit(data);
            if (typeof window !== 'undefined' && window.dispatchEvent) {
              try {
                window.dispatchEvent(new CustomEvent('maintenance:update', { detail: data }));
              } catch (err) {
                // ignore
              }
            }
          }
        } catch (err) {
          console.error('Failed to parse SSE maintenance event', err);
        }
      });

      this.eventSource.onerror = (err) => {
        // fallback to polling on error
        try {
          this.eventSource.close();
        } catch (e) {}
        this.eventSource = null;
        this.startPolling();
      };
    } catch (err) {
      // If SSE cannot be started, fall back to polling
      this.startPolling();
    }
  }

  stopSSE() {
    if (this.eventSource) {
      try {
        this.eventSource.close();
      } catch (e) {}
      this.eventSource = null;
    }
  }

  start() {
    // Prefer SSE, fall back to polling
    this.startSSE();
    // If SSE couldn't start, startSSE will start polling
    if (!this.eventSource) this.startPolling();
  }

  stop() {
    this.stopSSE();
    this.stopPolling();
  }

  onUpdate(cb) {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  emit(data) {
    this.listeners.forEach((cb) => {
      try {
        cb(data);
      } catch (e) {
        console.error('MaintenanceService listener error', e);
      }
    });
  }

  getCurrent() {
    return this.current;
  }
}

const maintenanceService = new MaintenanceService();
export default maintenanceService;
